/* eslint-disable max-lines */
import {
  CamFeedMode,
  UnifiedAlarm,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { WebrtcPlayer } from '@hakimo-ui/hakimo/ui-elements';
import { useFullscreen } from '@hakimo-ui/hakimo/util';
import clsx from 'clsx';
import { useLayoutEffect, useRef, useState } from 'react';

import { AlarmInfoCard } from './AlarmInfoCard';
import CamFeedActions from './CamFeedActions';
import CamPlaybackFeed from './CamPlaybackFeed';
import CellMediaControls from './CellMediaControls';
import { EventMedia } from './EventMedia';
import { groupHasMedia } from '../unified-alarm-group/utils';

interface Props {
  alarms: UnifiedAlarm[];
  onDoubleClick: () => void;
  timeZone: string;
  location: string;
  tenantId: string;
  alarmType?: string;
  product?: UnifiedMonitoringProduct;
  unificationEnabled?: boolean;
  onClickSafe?: () => void;
  onClickInvestigate?: () => void;
}

export function CellMedia(props: Props) {
  const {
    alarms,
    onDoubleClick,
    timeZone,
    location,
    tenantId,
    alarmType,
    product,
    unificationEnabled,
    onClickSafe,
    onClickInvestigate,
  } = props;

  const [mode, setMode] = useState<CamFeedMode>(CamFeedMode.EVENT_VIDEO);
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [playbackVideoSrc, setPlaybackVideoSrc] = useState<string>();
  const [isHovered, setIsHovered] = useState(false);
  const [currentPlayingAlarmId, setCurrentPlayingAlarmId] = useState<string>(
    () => {
      const sortedAlarms = alarms.sort(
        (a, b) => a.alarmStartTime - b.alarmStartTime
      );
      return sortedAlarms[0]?.id;
    }
  );

  const { isFullScreen, toggleFullScreen } = useFullscreen(containerRef);

  const firstAlarm = alarms[0];

  const {
    cameraLiveStreamUrl,
    cameraPlayBackUrl,
    media: { type: mediaType },
    alarm_source_system: alarmSourceSystem,
  } = firstAlarm;

  const showMatchedFaces = unificationEnabled || alarmSourceSystem === 'Vision';
  const shouldShowInfoCard =
    product === UnifiedMonitoringProduct.ENTERPRISE && !groupHasMedia(alarms);

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        const entry = entries[0];
        const parent = entry.target;
        setSize({
          width: parent ? parent.clientWidth - 4 : 0,
          height: parent ? parent.clientHeight - 4 : 0,
        });
      }
    });

    if (containerRef.current) {
      const parentDiv = containerRef.current.parentElement;
      parentDiv && resizeObserver.observe(parentDiv);
    }

    return () => {
      if (containerRef.current) {
        const parentDiv = containerRef.current.parentElement;
        parentDiv && resizeObserver.unobserve(parentDiv);
      }
    };
  }, []);

  const hideActionText =
    containerRef.current && containerRef.current.clientWidth < 280;

  const onUpdatePlaybackSrc = (params: string) => {
    setPlaybackVideoSrc(`${cameraPlayBackUrl}&${params}`);
    setMode(CamFeedMode.PLAYBACK);
  };

  const handleDoubleClick = () => onDoubleClick();
  const handleMouseOver = () => setIsHovered(true);
  const handleMouseOut = () => setIsHovered(false);

  const handlePlayingAlarmChange = (alarmId: string) => {
    alarmId && setCurrentPlayingAlarmId(alarmId);
  };

  return (
    <div
      className={clsx(
        'group/feed bg-ondark-bg-2 relative max-h-full max-w-full flex-grow rounded-md',
        isFullScreen && ''
      )}
      ref={containerRef}
      onDoubleClick={handleDoubleClick}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      style={{
        width: size.width,
        height: size.height,
      }}
    >
      {shouldShowInfoCard ? (
        <AlarmInfoCard
          alarm={firstAlarm}
          alarmType={alarmType}
          location={location}
        />
      ) : (
        <>
          {mode === CamFeedMode.LIVE && (
            <div className="h-full">
              <WebrtcPlayer url={cameraLiveStreamUrl} showControls={false} />
            </div>
          )}
          {mode === CamFeedMode.PLAYBACK && playbackVideoSrc && (
            <div className="h-full">
              <CamPlaybackFeed
                onUpdatePlaybackSrc={onUpdatePlaybackSrc}
                videoSrc={playbackVideoSrc}
              />
            </div>
          )}
          {mode === CamFeedMode.EVENT_VIDEO && (
            <EventMedia
              alarms={alarms}
              mediaType={mediaType as 'video' | 'frames'}
              timeZone={timeZone}
              currentPlayingAlarmId={currentPlayingAlarmId}
              onPlayingAlarmChange={handlePlayingAlarmChange}
              showMatchedFaces={showMatchedFaces}
            />
          )}
        </>
      )}
      <CellMediaControls
        alarms={alarms}
        camFeedMode={mode}
        onChangeCamFeedMode={setMode}
        tenantId={tenantId}
        onUpdatePlaybackSrc={onUpdatePlaybackSrc}
        isFullScreen={isFullScreen}
        toggleFullscreen={toggleFullScreen}
        showSeeker={isHovered}
        currentAlarmId={currentPlayingAlarmId}
        timeZone={timeZone}
        showInfoCard={shouldShowInfoCard}
      />
      <CamFeedActions
        hideActionText={!!hideActionText}
        onClickInvestigate={onClickInvestigate}
        onClickSafe={onClickSafe}
      />
    </div>
  );
}
